import { computed } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useRouteParams } from "@vueuse/router";
import { useExperienceRawApi } from "@/composables/useExperienceRawApi";
import { useMasterData } from "@/stores/master-data";
import { mapGenericToManageableHighlight } from "@/utils/mixed-highlights";
import type { MixedHighlightValue, HighlightsKey, HighlightsMasterDataKey, GenericHighlight } from "@/types/Highlights";
import type { RawSnapshot } from "@/types/generated/ExperienceRawServiceApi";

export const EXPERIENCE_RAW_FUNCTIONALS_QUERY_KEY = "experience-raw-functionals";

const masterDataStoreKeys: Record<HighlightsKey, HighlightsMasterDataKey> = {
  highlights: "highlights",
  included: "included",
  non_included: "included",
  important_information: "importantInformation",
};

const customFieldKeys: Record<
  HighlightsKey,
  "custom_highlights" | "custom_included" | "custom_non_included" | "custom_important_information"
> = {
  highlights: "custom_highlights",
  included: "custom_included",
  non_included: "custom_non_included",
  important_information: "custom_important_information",
};

export interface RawFunctionalsData {
  current: Record<HighlightsKey, MixedHighlightValue | undefined>;
  published: Record<HighlightsKey, MixedHighlightValue | undefined>;
  hasUnpublishedChanges: boolean;
}

function mapSnapshotToFunctionals(
  snapshot: RawSnapshot | undefined,
  masterData: ReturnType<typeof useMasterData>
): Record<HighlightsKey, MixedHighlightValue | undefined> {
  const keys: HighlightsKey[] = ["highlights", "included", "non_included", "important_information"];

  const result = {} as Record<HighlightsKey, MixedHighlightValue | undefined>;

  for (const key of keys) {
    const premadeCodes: string[] = snapshot?.raw?.functional?.[key] ?? [];
    const customItems = snapshot?.raw?.commercial?.[customFieldKeys[key]] ?? [];

    if (!premadeCodes.length && !customItems.length) {
      result[key] = undefined;
      continue;
    }

    const storeKey = masterDataStoreKeys[key];
    const premade: GenericHighlight[] = premadeCodes.map((code) => {
      try {
        return masterData.getHighlightByCode(storeKey, code);
      } catch {
        return { id: code, name: code, code, action: "NOOP" as const, visualization_order: 0 };
      }
    });

    const custom = mapGenericToManageableHighlight(customItems);

    result[key] = { premade, custom };
  }

  return result;
}

export function useCurrentExperienceRawFunctionalsQuery() {
  const experienceId = useRouteParams<string>("id");
  const experienceRawApi = useExperienceRawApi();
  const masterData = useMasterData();

  return useQuery({
    queryKey: [EXPERIENCE_RAW_FUNCTIONALS_QUERY_KEY, experienceId],
    queryFn: async (): Promise<RawFunctionalsData> => {
      const [{ data: rawSnapshot }, { data: publishedSnapshot }] = await Promise.all([
        experienceRawApi.getMostRecentRawSnapshot(experienceId.value),
        experienceRawApi.getMostRecentPublishedRawSnapshot(experienceId.value),
      ]);

      return {
        current: mapSnapshotToFunctionals(rawSnapshot, masterData),
        published: mapSnapshotToFunctionals(publishedSnapshot, masterData),
        hasUnpublishedChanges: !!publishedSnapshot && rawSnapshot?.raw?.status_code !== "UP_TO_DATE",
      };
    },
    enabled: computed(() => !!experienceId.value),
  });
}
